import { Schema } from "effect";
import { AccountId, ShopperAiIntegrationId } from "../../../ids/definitions";
import {
  AttentiveChannelData,
  AttentiveIntegrationType,
  FacebookChannelData,
  FacebookIntegrationType,
  GmailChannelData,
  GmailIntegrationType,
  InstagramChannelData,
  InstagramIntegrationType,
  NumberIntegrationType,
  OutlookChannelData,
  OutlookIntegrationType,
  PostscriptChannelData,
  PostscriptIntegrationType,
  RedoChatAiChannelData,
  RedoChatAiIntegrationType,
  RedoChatFlowChannelData,
  RedoChatFlowIntegrationType,
  RedoChatLiveChannelData,
  RedoChatLiveIntegrationType,
  TiktokChannelData,
  TiktokIntegrationType,
} from "./channel-data";

export const ShopperAiIntegrationType = Schema.Union(
  GmailIntegrationType,
  OutlookIntegrationType,
  InstagramIntegrationType,
  FacebookIntegrationType,
  TiktokIntegrationType,
  AttentiveIntegrationType,
  NumberIntegrationType,
  PostscriptIntegrationType,
  RedoChatAiIntegrationType,
  RedoChatFlowIntegrationType,
  RedoChatLiveIntegrationType,
);
export type ShopperAiIntegrationType = typeof ShopperAiIntegrationType.Type;

// Base integration data shared by all integration types
const BaseIntegrationData = Schema.Struct({
  id: ShopperAiIntegrationId,
  accountId: AccountId,
  permissionsRevoked: Schema.Boolean,
});

// Individual integration response types (discriminated by integrationType)
const GmailIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: GmailIntegrationType,
  channelData: Schema.NullOr(GmailChannelData),
});

const OutlookIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: OutlookIntegrationType,
  channelData: Schema.NullOr(OutlookChannelData),
});

const FacebookIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: FacebookIntegrationType,
  channelData: Schema.NullOr(FacebookChannelData),
});

const InstagramIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: InstagramIntegrationType,
  channelData: Schema.NullOr(InstagramChannelData),
});

const AttentiveIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: AttentiveIntegrationType,
  channelData: Schema.NullOr(AttentiveChannelData),
});

const PostscriptIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: PostscriptIntegrationType,
  channelData: Schema.NullOr(PostscriptChannelData),
});

const TiktokIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: TiktokIntegrationType,
  channelData: Schema.NullOr(TiktokChannelData),
});

const RedoChatAiIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatAiIntegrationType,
  channelData: Schema.NullOr(RedoChatAiChannelData),
});

const RedoChatFlowIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatFlowIntegrationType,
  channelData: Schema.NullOr(RedoChatFlowChannelData),
});

const RedoChatLiveIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatLiveIntegrationType,
  channelData: Schema.NullOr(RedoChatLiveChannelData),
});

// Response schema for integration with channel data (discriminated union by integrationType)
export const IntegrationWithChannelDataResponse = Schema.Union(
  GmailIntegrationWithChannelDataResponse,
  OutlookIntegrationWithChannelDataResponse,
  FacebookIntegrationWithChannelDataResponse,
  InstagramIntegrationWithChannelDataResponse,
  AttentiveIntegrationWithChannelDataResponse,
  PostscriptIntegrationWithChannelDataResponse,
  TiktokIntegrationWithChannelDataResponse,
  RedoChatAiIntegrationWithChannelDataResponse,
  RedoChatFlowIntegrationWithChannelDataResponse,
  RedoChatLiveIntegrationWithChannelDataResponse,
);
export type IntegrationWithChannelDataResponse =
  typeof IntegrationWithChannelDataResponse.Type;

export const IntegrationCursor = Schema.Struct({ id: ShopperAiIntegrationId });
export type IntegrationCursor = typeof IntegrationCursor.Type;
